import { isTabletDown, isTabletUp } from '../Utility/Breakpoint'
import { getClosestElement, getElement, getElementChildren, getElements } from '../Utility/Dom'
import { IS_ACTIVE } from '../Utility/State'

class NavMainDesktop {
  private readonly closeButtons: NodeListOf<HTMLElement>
  private readonly indicator: HTMLElement | null
  private lastWrapWidth = 0
  private readonly layerWrap: HTMLElement | null
  private readonly navButtons: NodeListOf<HTMLElement>
  private readonly navItems: NodeListOf<HTMLElement>
  private readonly tabButtons: NodeListOf<HTMLElement>

  constructor() {
    this.tabButtons = getElements('nav-tab-button')
    this.layerWrap = getElement('nav-layer-wrap')
    this.indicator = getElement('nav-main-indicator')
    this.navItems = getElements('nav-main-item')
    this.navButtons = getElements('nav-layer-button')
    this.closeButtons = getElements('nav-layer-close')
  }

  public init(): void {
    this.addNavButtonEvents()
    this.addTabEvents()
    this.addIndicatorEvents()
    this.addCloseEvents()
  }

  private activateTab(activeButton: HTMLElement): void {
    const tabsContainer = getClosestElement(activeButton, 'nav-tabs-container')

    if (tabsContainer === null) {
      return
    }

    const containerButtons = getElementChildren(tabsContainer, 'nav-tab-button')

    containerButtons.forEach((btn): void => {
      btn.setAttribute('aria-expanded', 'false')
    })

    activeButton.setAttribute('aria-expanded', 'true')
  }

  private addCloseEvents(): void {
    this.closeButtons.forEach((closeButton): void => {
      closeButton.addEventListener('click', (): void => {
        if (isTabletDown()) {
          return
        }

        const trigger = this.findTriggerForCloseButton(closeButton)

        this.closeAllDropdowns()
        trigger?.focus()
      })
    })
  }

  private addIndicatorEvents(): void {
    if (this.layerWrap === null || this.indicator === null || this.navItems.length === 0) {
      return
    }

    const initialActiveItem = this.getActiveNavItem()

    if (initialActiveItem !== null) {
      this.updateIndicator(initialActiveItem)
    }

    this.navItems.forEach((item): void => {
      item.addEventListener('mouseenter', (): void => {
        if (isTabletUp()) {
          this.updateIndicator(item)
        }
      })

      item.addEventListener('focusin', (): void => {
        if (isTabletUp()) {
          this.updateIndicator(item)
        }
      })
    })

    this.layerWrap.addEventListener('mouseleave', (): void => {
      if (isTabletUp()) {
        const activeItem = this.getActiveNavItem()

        if (activeItem !== null) {
          this.updateIndicator(activeItem)
        }
      }
    })

    this.lastWrapWidth = this.layerWrap.getBoundingClientRect().width

    const resizeObserver = new ResizeObserver((): void => {
      if (this.layerWrap === null) {
        return
      }

      const width = this.layerWrap.getBoundingClientRect().width

      if (width === this.lastWrapWidth) {
        return
      }

      this.lastWrapWidth = width

      if (isTabletUp()) {
        const activeItem = this.getActiveNavItem()

        if (activeItem !== null) {
          this.updateIndicator(activeItem)
        }
      }
    })

    resizeObserver.observe(this.layerWrap)
  }

  private addNavButtonEvents(): void {
    if (this.navButtons.length === 0) {
      return
    }

    this.navButtons.forEach((button): void => {
      button.addEventListener('click', (event): void => {
        if (isTabletDown()) {
          return
        }

        event.preventDefault()
        const isExpanded = button.getAttribute('aria-expanded') === 'true'

        this.closeAllDropdowns()

        if (isExpanded === false) {
          button.setAttribute('aria-expanded', 'true')
        }
      })
    })

    document.addEventListener('keydown', (event): void => {
      if (event.key === 'Escape' && isTabletUp()) {
        const expanded = Array.from(this.navButtons).find((btn): boolean => btn.getAttribute('aria-expanded') === 'true')

        this.closeAllDropdowns()
        expanded?.focus()
      }
    })

    document.addEventListener('click', (event): void => {
      if (isTabletDown()) {
        return
      }

      const target = event.target as HTMLElement
      const isInsideNav = getClosestElement(target, 'nav-main-item')

      if (isInsideNav === null) {
        this.closeAllDropdowns()
      }
    })

    document.addEventListener('focusin', (event): void => {
      if (isTabletDown()) {
        return
      }

      const target = event.target as HTMLElement
      const navMain = getElement('nav-main')

      if (navMain !== null && navMain.contains(target) === false) {
        this.closeAllDropdowns()
      }
    })
  }

  private addTabEvents(): void {
    if (this.tabButtons.length === 0) {
      return
    }

    this.tabButtons.forEach((button): void => {
      button.addEventListener('click', (): void => {
        if (isTabletDown()) {
          return
        }

        this.activateTab(button)
      })

      button.addEventListener('focusin', (): void => {
        if (isTabletDown()) {
          return
        }

        this.activateTab(button)
      })
    })
  }

  private closeAllDropdowns(): void {
    this.navButtons.forEach((button): void => {
      button.setAttribute('aria-expanded', 'false')
    })
  }

  private findTriggerForCloseButton(closeButton: HTMLElement): HTMLElement | null {
    const navItem = getClosestElement(closeButton, 'nav-main-item')

    if (navItem === null) {
      return null
    }

    return navItem.querySelector<HTMLElement>('[data-js="nav-layer-button"]')
  }

  private getActiveNavItem(): HTMLElement | null {
    for (const item of this.navItems) {
      const activeElement = item.querySelector(`[data-js="nav-layer-button"].${IS_ACTIVE}, [data-js="nav-main-link"].${IS_ACTIVE}`)

      if (activeElement !== null) {
        return item
      }
    }

    return null
  }

  private updateIndicator(item: HTMLElement): void {
    if (this.layerWrap === null || this.indicator === null) {
      return
    }

    const button = item.querySelector<HTMLElement>('[data-js="nav-layer-button"], [data-js="nav-main-link"]')

    if (button === null) {
      return
    }

    const buttonRect = button.getBoundingClientRect()
    const wrapRect = this.layerWrap.getBoundingClientRect()

    this.layerWrap.style.setProperty('--indicator-width', `${buttonRect.width}px`)
    this.layerWrap.style.setProperty('--indicator-offset', `${buttonRect.left - wrapRect.left}px`)
  }
}

const navMainDesktop = new NavMainDesktop()
navMainDesktop.init()
